
import React, { useEffect, useMemo, useState } from "react";
import InstituteSidebar from "./Institutesidebar";
import {
  getAllPurchases,
  getInstitutePayments,
  submitInstitutePayment,
} from "../api/instituteclient";

export default function Institutepayments() {
  const [registrations, setRegistrations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState("UPI");
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError("");

    try {
      const [registrationResponse, paymentResponse] = await Promise.all([
        getAllPurchases(),
        getInstitutePayments(),
      ]);

      setRegistrations(
        Array.isArray(registrationResponse.data)
          ? registrationResponse.data
          : registrationResponse.data?.results || []
      );

      setPayments(
        Array.isArray(paymentResponse.data)
          ? paymentResponse.data
          : paymentResponse.data?.results || []
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Unable to load institute payment information."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const eligibleRegistrations = useMemo(() => {
    return registrations.filter((item) => {
      const registrationId = item.id;
      const status = String(item.status || "").toUpperCase();

      const alreadyPaid = status === "PAID" ||
        status === "SCHEDULED" ||
        status === "READY" ||
        status === "IN_PROGRESS" ||
        status === "SUBMITTED" ||
        status === "AUTO_SUBMITTED" ||
        status === "TERMINATED";

      const hasPendingPayment = payments.some(
        (payment) =>
          payment.status === "PENDING" &&
          payment.registrations?.some(
            (registration) =>
              Number(registration.registration_id) === Number(registrationId)
          )
      );

      return !alreadyPaid && !hasPendingPayment;
    });
  }, [registrations, payments]);

  const total = useMemo(() => {
    return eligibleRegistrations
      .filter((item) => selected.includes(item.id))
      .reduce(
        (sum, item) => sum + Number(item.fee || 0),
        0
      );
  }, [eligibleRegistrations, selected]);

  const toggleRegistration = (id) => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!selected.length) {
      setError("Select at least one exam registration.");
      return;
    }

    if (!reference.trim()) {
      setError("Enter your UPI or bank transaction reference.");
      return;
    }

    const formData = new FormData();
    formData.append("payment_method", method);
    formData.append("transaction_reference", reference.trim());
    formData.append("registration_ids", JSON.stringify(selected));

    if (proof) {
      formData.append("payment_proof", proof);
    }

    setSubmitting(true);

    try {
      const response = await submitInstitutePayment(formData);

      setMessage(
        response.data?.message ||
          "Payment submitted for admin verification."
      );

      setSelected([]);
      setReference("");
      setProof(null);

      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Unable to submit payment. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "—"
      : date.toLocaleString();
  };

  const money = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const statusClass = (status) => {
    const value = String(status || "").toUpperCase();

    if (value === "SUCCESS") return "success";
    if (value === "REJECTED" || value === "FAILED") return "danger";
    if (value === "PENDING") return "pending";

    return "neutral";
  };

  return (
    <div className="admin-layout d-flex">
      <InstituteSidebar />

      <main className="admin-main flex-grow-1 p-3 p-lg-4">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1">Institute Payments</h2>
              <p className="text-muted mb-0">
                Manage exam registration payments and transaction history.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={loadData}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-2" />
              Refresh
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {message && (
            <div className="alert alert-success" role="alert">
              {message}
            </div>
          )}

          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="text-muted small">Available Registrations</div>
                  <div className="fs-3 fw-bold">
                    {loading ? "—" : eligibleRegistrations.length}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="text-muted small">Selected Total</div>
                  <div className="fs-3 fw-bold">{money(total)}</div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="text-muted small">Transactions</div>
                  <div className="fs-3 fw-bold">{payments.length}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0 fw-bold">Select Exam Registrations</h5>
            </div>

            <div className="card-body">
              {loading ? (
                <p className="text-muted mb-0">Loading registrations...</p>
              ) : eligibleRegistrations.length === 0 ? (
                <p className="text-muted mb-0">
                  No unpaid registrations are currently available.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Student</th>
                        <th>Exam</th>
                        <th>Status</th>
                        <th className="text-end">Fee</th>
                      </tr>
                    </thead>

                    <tbody>
                      {eligibleRegistrations.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selected.includes(item.id)}
                              onChange={() => toggleRegistration(item.id)}
                              aria-label={`Select registration ${item.id}`}
                            />
                          </td>

                          <td>
                            <div className="fw-semibold">
                              {item.studentName || item.studentName === ""
                                ? item.studentName || "Student"
                                : item.student_name || item.email || "Student"}
                            </div>
                            <small className="text-muted">
                              Registration #{item.id}
                            </small>
                          </td>

                          <td>{item.examName || item.exam_name || `Exam #${item.examId || item.exam_id || ""}`}</td>

                          <td>
                            <span className="badge text-bg-light">
                              {item.status || "REGISTERED"}
                            </span>
                          </td>

                          <td className="text-end fw-semibold">
                            {money(item.fee ?? item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0 fw-bold">Submit Manual Payment</h5>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-select"
                      value={method}
                      onChange={(event) => setMethod(event.target.value)}
                    >
                      <option value="UPI">UPI</option>
                      <option value="BANK_TRANSFER">Bank Transfer</option>
                    </select>
                  </div>

                  <div className="col-md-8">
                    <label className="form-label">
                      Transaction Reference *
                    </label>
                    <input
                      className="form-control"
                      value={reference}
                      onChange={(event) => setReference(event.target.value)}
                      placeholder="Enter UPI transaction ID or bank reference"
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      Payment Proof (optional)
                    </label>
                    <input
                      className="form-control"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(event) =>
                        setProof(event.target.files?.[0] || null)
                      }
                    />
                  </div>

                  <div className="col-12">
                    <div className="alert alert-info mb-0">
                      Selected registrations: <strong>{selected.length}</strong>
                      {" · "}
                      Total: <strong>{money(total)}</strong>
                      <div className="small mt-1">
                        This submission remains pending until the Main Admin
                        verifies it.
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={submitting || loading}
                    >
                      {submitting ? "Submitting..." : "Submit for Verification"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0 fw-bold">Transaction History</h5>
            </div>

            <div className="card-body">
              {loading ? (
                <p className="text-muted mb-0">Loading transactions...</p>
              ) : payments.length === 0 ? (
                <p className="text-muted mb-0">
                  No institute transactions yet.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th>Transaction</th>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Registrations</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Admin Note</th>
                      </tr>
                    </thead>

                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>#{payment.id}</td>
                          <td>{formatDate(payment.created_at)}</td>
                          <td>{payment.payment_method}</td>
                          <td>
                            {payment.registrations?.length || 0}
                          </td>
                          <td className="fw-semibold">
                            {money(payment.amount)}
                          </td>
                          <td>
                            <span
                              className={`badge rounded-pill text-bg-${statusClass(payment.status) === "success" ? "success" : statusClass(payment.status) === "danger" ? "danger" : statusClass(payment.status) === "pending" ? "warning" : "secondary"}`}
                            >
                              {payment.status}
                            </span>
                          </td>
                          <td>{payment.admin_note || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
